import { useState } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import Home from "lucide-react/dist/esm/icons/home.js";
import PlusCircle from "lucide-react/dist/esm/icons/plus-circle.js";
import Brain from "lucide-react/dist/esm/icons/brain.js";
import Users from "lucide-react/dist/esm/icons/users.js";
import User from "lucide-react/dist/esm/icons/user.js";
import Utensils from "lucide-react/dist/esm/icons/utensils.js";
import Stethoscope from "lucide-react/dist/esm/icons/stethoscope.js";
import Pill from "lucide-react/dist/esm/icons/pill.js";
import Sun from "lucide-react/dist/esm/icons/sun.js";
import Mic from "lucide-react/dist/esm/icons/mic.js";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@shared/ui/sheet/Sheet";
import { activeLanguagePrefix } from "@shared/lib/localized-path";
import { cn } from "@shared/lib/utils";
import { useAIFeatureEnabled, usePaymentConfig } from "@shared/hooks/use-paywall";
import { useSiteFeatureFlags } from "@shared/hooks/use-site-feature-flags";
import { useLandingPath } from "@shared/hooks/use-site-feature-flags/useLandingPath";
import { isPathFeatureEnabled, type SiteFeatureFlags } from "@shared/lib/site-features";

interface Tab {
  id: string;
  labelKey: string;
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  isTrack?: boolean;
  /**
   * Route prefixes this tab owns. Defaults to `[href]`. A tab stays active on
   * any of them (and their children), e.g. Coach owns both AI coaching and the
   * health assistant / voice log.
   */
  matchPrefixes?: readonly string[];
}

// The Home tab's href is the landing page (first enabled feature), filled in
// at render time; every other tab is hidden when its feature is disabled.
const TABS: Tab[] = [
  { id: "home", labelKey: "bottom_nav.home", href: "/dashboard", Icon: Home },
  { id: "track", labelKey: "bottom_nav.track", href: "#track", Icon: PlusCircle, isTrack: true },
  {
    id: "coach",
    labelKey: "bottom_nav.coach",
    href: "/ai-coaching",
    Icon: Brain,
    matchPrefixes: ["/ai-coaching", "/health-assistant"],
  },
  { id: "community", labelKey: "bottom_nav.community", href: "/community", Icon: Users },
  { id: "profile", labelKey: "bottom_nav.profile", href: "/profile", Icon: User },
];

const SUPPORTED_LANGUAGES = ["en", "ar", "de", "es", "fr"] as const;
type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

const QUICK_ADD_DESCRIPTION_FALLBACKS: Record<SupportedLanguage, string> = {
  en: "Choose what kind of health entry to log.",
  ar: "اختر نوع الإدخال الصحي الذي تريد تسجيله.",
  de: "Wählen Sie aus, welche Art von Gesundheitseintrag Sie erfassen möchten.",
  es: "Elige qué tipo de registro de salud quieres añadir.",
  fr: "Choisissez le type d’entrée de santé à enregistrer.",
};

function getSupportedLanguage(language?: string): SupportedLanguage {
  const baseLanguage = language?.split("-")[0];
  return SUPPORTED_LANGUAGES.includes(baseLanguage as SupportedLanguage)
    ? (baseLanguage as SupportedLanguage)
    : "en";
}

function localizeHref(href: string, pathname: string): string {
  const prefix = activeLanguagePrefix(pathname);
  if (!prefix || href === "/" || href === prefix || href.startsWith(`${prefix}/`)) {
    return href;
  }
  return `${prefix}${href}`;
}

export function isActiveTab(tab: Pick<Tab, "href" | "matchPrefixes">, pathname: string): boolean {
  if (tab.href === "#track") return false;
  const clean = pathname.replace(/^\/[a-z]{2}(?=\/|$)/, "") || "/";
  const prefixes = tab.matchPrefixes ?? [tab.href];
  return prefixes.some(
    (prefix) => clean === prefix || clean.startsWith(prefix + "/"),
  );
}

export function BottomNav({ hidden = false }: { hidden?: boolean } = {}) {
  const { t } = useTranslation("mobile");
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const navigate = useNavigate();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;

  // The "Coach" tab points at /ai-coaching — hide it when the paywall is off or
  // the ai_coaching feature is disabled, mirroring the desktop nav filter.
  const { enabled: coachEnabled } = useAIFeatureEnabled("ai_coaching");
  const { data: flags } = useSiteFeatureFlags();
  const landingPath = useLandingPath();
  const quickAddItems = useQuickAddItems(flags);
  // Hidden tabs are removed, not left as empty slots — the row reflows.
  const tabs = TABS.map((tab) =>
    tab.id === "home" ? { ...tab, href: landingPath, matchPrefixes: [landingPath] } : tab,
  ).filter((tab) => {
    if (tab.id === "coach" && !coachEnabled) return false;
    if (tab.isTrack) return quickAddItems.length > 0;
    return isPathFeatureEnabled(tab.href, flags);
  });

  const handleTabClick = (tab: Tab) => {
    if (tab.isTrack) {
      setQuickAddOpen(true);
      return;
    }
    void navigate({ to: localizeHref(tab.href, pathname) as never });
  };

  return (
    <>
      <nav
        role="navigation"
        aria-label={t("bottom_nav.home")}
        data-testid="bottom-nav"
        hidden={hidden}
        className={cn(
          "fixed bottom-0 start-0 end-0 z-40 flex items-stretch border-t border-border bg-background",
          // `hidden` alone loses to `flex`; the on-screen keyboard is open.
          hidden && "hidden",
        )}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {tabs.map((tab) => {
          const active = isActiveTab(tab, pathname);
          return (
            <button
              key={tab.id}
              type="button"
              data-testid={`bottom-nav-tab-${tab.id}`}
              aria-label={t(tab.labelKey)}
              aria-current={active ? "page" : undefined}
              onClick={() => handleTabClick(tab)}
              className={cn(
                "flex min-h-[56px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                active
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <tab.Icon
                className={cn("h-5 w-5 shrink-0", active && "text-primary")}
                aria-hidden="true"
              />
              <span className="block w-full max-w-full truncate text-center leading-tight">
                {t(tab.labelKey)}
              </span>
            </button>
          );
        })}
      </nav>

      <QuickAddSheet
        open={quickAddOpen}
        onOpenChange={setQuickAddOpen}
        items={quickAddItems}
      />
    </>
  );
}

interface QuickAddItem {
  key: string;
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
}

interface QuickAddSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: readonly QuickAddItem[];
}

/**
 * Quick-add entries whose feature is enabled. Voice additionally needs its AI
 * entitlement (`health_assistant_voice`) — flags decide exposure, the paywall
 * decides entitlement, and both must pass.
 */
function useQuickAddItems(flags: SiteFeatureFlags | undefined): QuickAddItem[] {
  const { t } = useTranslation("mobile");
  const { data: paymentConfig } = usePaymentConfig();
  const voiceAvailable = Boolean(
    (paymentConfig?.paymentsEnabled || paymentConfig?.byokEnabled) &&
      paymentConfig?.aiFeaturesEnabled?.health_assistant_voice,
  );
  const candidates: QuickAddItem[] = [
    {
      key: "food",
      href: "/diet-protocols/food-log",
      Icon: Utensils,
      label: t("quick_add.food"),
    },
    {
      key: "symptom",
      href: "/symptoms/log",
      Icon: Stethoscope,
      label: t("quick_add.symptom"),
    },
    {
      key: "supplement",
      href: "/supplements/log",
      Icon: Pill,
      label: t("quick_add.supplement"),
    },
    {
      key: "lifestyle",
      href: "/lifestyle/activities",
      Icon: Sun,
      label: t("quick_add.lifestyle"),
    },
    ...(voiceAvailable
      ? [
          {
            key: "voice",
            href: "/health-assistant/voice-log",
            Icon: Mic,
            label: t("quick_add.voice"),
          },
        ]
      : []),
  ];
  return candidates.filter((item) => isPathFeatureEnabled(item.href, flags));
}

function QuickAddSheet({ open, onOpenChange, items: quickAddItems }: QuickAddSheetProps) {
  const { t, i18n } = useTranslation("mobile");
  const navigate = useNavigate();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  const language = getSupportedLanguage(i18n.resolvedLanguage || i18n.language);
  const quickAddDescription = t(
    "quick_add.description",
    QUICK_ADD_DESCRIPTION_FALLBACKS[language],
  );

  const handleQuickAdd = (href: string) => {
    onOpenChange(false);
    void navigate({ to: localizeHref(href, pathname) as never });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl px-4 pb-safe-b pt-4"
        closeLabel={t("header.close")}
        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
      >
        <SheetHeader className="mb-4">
          <SheetTitle className="text-start">{t("quick_add.title")}</SheetTitle>
          <SheetDescription className="sr-only">
            {quickAddDescription}
          </SheetDescription>
        </SheetHeader>
        <div className="grid grid-cols-2 gap-3 pb-2">
          {quickAddItems.map(({ key, href, Icon, label }) => (
            <button
              key={key}
              type="button"
              data-testid={`quick-add-${key}`}
              onClick={() => handleQuickAdd(href)}
              className="flex min-h-[64px] flex-col items-center justify-center gap-2 rounded-xl border border-border bg-card px-3 py-4 text-sm font-medium text-card-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Icon className="h-6 w-6" aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
